import { css } from 'aphrodite';
import * as React from 'react';
import {
	Dropdown,
	DropdownContent,
	DropdownGroup,
	DropdownGroupLabel,
	DropdownItem,
	DropdownSeparator,
	DropdownTrigger,
} from '../../../../web/components-v2/Dropdown';
import { useAppDrawerContext } from '../../../../web/components-v2/Drawer/AppDrawer';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../../web/components/svgs/icons/SparklesIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../web/components/Tooltip';
import { TYSON_COACHING_DRAWER_VALUE } from '../../../components/Queue';
import { useQueue } from '../../../hooks/queue';
import { useTysonCoaching } from '../../contexts/TysonCoachingContext';
import { useTysonCoachingConversations } from '../../hooks/useTysonCoachingConversations';
import { styleSheet } from './styles';

interface TysonCoachingDropdownProps {
	disabled?: boolean;
}

export const TysonCoachingDropdown: React.FC<TysonCoachingDropdownProps> = ({ disabled }) => {
	const coaching = useTysonCoaching();
	const appDrawerContext = useAppDrawerContext();
	const { data: conversations, isLoading } = useTysonCoachingConversations();
	const queue = useQueue();

	const contactId = queue?.lead?.contacts?.[0]?.id;

	const handleStartNewSession = React.useCallback(async () => {
		if (!queue?.companyId || !contactId) return;

		// Stop any existing session before starting a new one
		if (coaching.isActive) {
			coaching.stopCoaching();
		}

		await coaching.startCoaching({
			companyId: queue.companyId,
			contactId,
			leadServedId: queue.leadServedId,
		});
		appDrawerContext.setValue(TYSON_COACHING_DRAWER_VALUE);
	}, [queue, contactId, coaching, appDrawerContext]);

	const handleOpenCoachingPanel = React.useCallback(() => {
		if (coaching.isActive) {
			// Toggle the panel if coaching is active
			if (appDrawerContext.value === TYSON_COACHING_DRAWER_VALUE) {
				appDrawerContext.setValue('');
			} else {
				appDrawerContext.setValue(TYSON_COACHING_DRAWER_VALUE);
			}
		}
	}, [coaching.isActive, appDrawerContext]);

	const handleViewConversation = React.useCallback(
		(conversationId: string) => {
			coaching.viewHistoricalConversation(conversationId);
			appDrawerContext.setValue(TYSON_COACHING_DRAWER_VALUE);
		},
		[appDrawerContext, coaching]
	);

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		if (diffDays === 0) {
			return `Today at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
		} else if (diffDays === 1) {
			return `Yesterday at ${date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
		} else if (diffDays < 7) {
			return date.toLocaleDateString([], { weekday: 'long', hour: 'numeric', minute: '2-digit' });
		}
		return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
	};

	const recentConversations = React.useMemo(() => {
		if (!conversations) return [];
		// Sort by creation date descending and take the first 10
		return [...conversations]
			.sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime())
			.slice(0, 10);
	}, [conversations]);

	const isCoachingPanelOpen = appDrawerContext.value === TYSON_COACHING_DRAWER_VALUE;

	return (
		<Dropdown>
			<Tooltip>
				<TooltipTrigger asChild>
					<span>
						<DropdownTrigger
							className={css(
								styleSheet.trigger,
								isCoachingPanelOpen && styleSheet.triggerActive,
								disabled && styleSheet.triggerDisabled
							)}
							disabled={disabled}
						>
							<SparklesIcon className={css(styleSheet.icon)} />
						</DropdownTrigger>
					</span>
				</TooltipTrigger>
				<TooltipContent side='left'>AI Coaching</TooltipContent>
			</Tooltip>

			<DropdownContent align='end' sideOffset={8}>
				{isCoachingPanelOpen && (
					<>
						<DropdownItem onClick={() => appDrawerContext.setValue('')}>Close drawer</DropdownItem>
						<DropdownSeparator />
					</>
				)}
				<DropdownItem onClick={handleStartNewSession} disabled={!contactId}>
					<span>New Session</span>
				</DropdownItem>
				{coaching.isActive && (
					<DropdownItem onClick={handleOpenCoachingPanel} styles={[styleSheet.activeSessionItem]}>
						<div className={css(styleSheet.activeSessionIndicator)} />
						<span>Current Session</span>
						{coaching.isAIResponding && <LoadingSpinner type='small' />}
					</DropdownItem>
				)}
				<DropdownSeparator />

				<DropdownGroup>
					<DropdownGroupLabel>Previous Sessions</DropdownGroupLabel>
					{isLoading ? (
						<div className={css(styleSheet.loadingContainer)}>
							<LoadingSpinner type='small' />
							<span>Loading...</span>
						</div>
					) : recentConversations.length === 0 ? (
						<div className={css(styleSheet.emptyState)}>No previous coaching sessions</div>
					) : (
						recentConversations.map(conversation => (
							<DropdownItem
								key={conversation.id}
								onClick={() => handleViewConversation(conversation.id)}
								styles={[styleSheet.conversationItem]}
							>
								<div className={css(styleSheet.conversationInfo)}>
									<span className={css(styleSheet.conversationDate)}>{formatDate(conversation.creationDate)}</span>
								</div>
							</DropdownItem>
						))
					)}
				</DropdownGroup>
			</DropdownContent>
		</Dropdown>
	);
};
